import type { IGeocoder } from '../data/interfaces/IGeocoder';
import type { Location } from '../domain/Location';
import type { GeocodeProgress } from '../domain/GeocodeProgress';
import { GeoCache } from '../utils/GeoCache';
import { RateLimiter } from '../utils/RateLimiter';
import { LocationNormalizer } from '../utils/LocationNormalizer';
import { buildQueries, selectAcceptable, type NominatimResult } from '../utils/PlaceQueryBuilder';
import { expectedRegion, withinRegion } from '../utils/RegionCheck';
import { createLogger } from '../utils/logger';

const log = createLogger('GeocoderService');

const NOMINATIM_BASE = 'https://nominatim.openstreetmap.org/search';
const RETRY_DELAY_MS = 2000;

/** Coordinates for a place; `approximate` when only a broader area matched. */
export interface ResolvedCoords {
  lat: number;
  lng: number;
  approximate?: boolean;
  /** Hand-verified override — never flagged unverified. */
  verified?: boolean;
}

interface FetchResult {
  /** null = transient failure (network, HTTP error, 429 after retry, malformed JSON). */
  results: NominatimResult[] | null;
  hit429: boolean;
}

interface ResolveResult {
  coords: ResolvedCoords | null;
  hit429: boolean;
  /** True when a lookup got no usable response — the place was not judged, only skipped. */
  transient: boolean;
}

/** Consecutive transient places after which a batch stops asking Nominatim. */
const MAX_TRANSIENT_STREAK = 3;

/**
 * Resolves GEDCOM place strings to lat/lng coordinates.
 *
 * Lookup order: seed cache → GeoCache (localStorage) → failure cache → Nominatim.
 * Cached coordinates outside the region the place names are ignored, so stale
 * wrong entries self-heal. Nominatim is tried with fallback query variants
 * (PlaceQueryBuilder) and only place/boundary results inside the named region are
 * accepted. Places no variant resolves are recorded in the failure cache;
 * transient errors are not — those places are counted as deferred, and after
 * MAX_TRANSIENT_STREAK in a row the batch stops asking Nominatim (cached places
 * still resolve). A resolved place whose string names no region could not be
 * region-checked; it is marked `unverified` and reported through onUnverified.
 *
 * The seed cache is passed in at construction as a plain Map so this service
 * is testable without a real JSON file.
 *
 * Does NOT import AppStore — callers wire AppStore updates via callbacks.
 */
export class GeocoderService implements IGeocoder {
  private readonly seedCache: Map<string, ResolvedCoords>;
  private readonly geoCache: GeoCache;
  private readonly rateLimiter: RateLimiter;
  private readonly normalizer: LocationNormalizer;

  constructor(
    seedCache: Map<string, ResolvedCoords>,
    geoCache: GeoCache,
    rateLimiter: RateLimiter,
    normalizer: LocationNormalizer,
  ) {
    this.seedCache = seedCache;
    this.geoCache = geoCache;
    this.rateLimiter = rateLimiter;
    this.normalizer = normalizer;
  }

  /** IGeocoder single-lookup. No progress tracking. */
  async geocode(placeName: string): Promise<{ lat: number; lng: number } | null> {
    const normalized = this.normalizer.normalize(placeName);
    if (normalized === null) {
      return null;
    }

    const cached = this._cachedLookup(normalized);
    if (cached !== null) {
      return { lat: cached.lat, lng: cached.lng };
    }
    if (this.geoCache.getFailure(normalized)) {
      return null;
    }

    const { coords } = await this._resolve(normalized);
    return coords === null ? null : { lat: coords.lat, lng: coords.lng };
  }

  /**
   * Batch geocode. Deduplicates by normalized place name. Mutates Location
   * objects in place with resolved coordinates, then returns the same array.
   *
   * Callers MUST issue AppStore.setState({ arcs: [...updatedArcs] }) after
   * this resolves to notify Observable subscribers (new reference required).
   */
  async geocodeLocations(
    locations: Location[],
    opts: {
      onProgress?: (p: GeocodeProgress) => void;
      onFailure?: (raw: string) => void;
      onUnverified?: (raw: string) => void;
    } = {},
  ): Promise<Location[]> {
    const groups = new Map<string, Location[]>();

    for (const loc of locations) {
      if (loc.geocoded) {
        continue;
      }
      const normalized = this.normalizer.normalize(loc.raw);
      if (normalized === null) {
        continue;
      }
      const group = groups.get(normalized) ?? [];
      group.push(loc);
      groups.set(normalized, group);
    }

    const total = groups.size;
    let resolved = 0;
    let failed = 0;
    let deferred = 0;
    let cacheHits = 0;
    let rateLimitHit = false;
    let transientStreak = 0;
    let networkPaused = false;
    const report = (): void =>
      opts.onProgress?.({ resolved, total, failed, deferred, cacheHits, rateLimitHit });

    report();

    for (const [normalized, group] of groups) {
      const cached = this._cachedLookup(normalized);
      if (cached !== null) {
        this._applyCoords(group, cached);
        if (cached.verified !== true) {
          this._flagUnverified(normalized, group, opts.onUnverified);
        }
        resolved++;
        cacheHits++;
        report();
        continue;
      }

      let coords: ResolvedCoords | null = null;
      let transient = false;
      if (this.geoCache.getFailure(normalized)) {
        log.debug(`Failure cache hit: "${normalized}" — skipping Nominatim`);
      } else if (networkPaused) {
        transient = true;
      } else {
        const result = await this._resolve(normalized);
        coords = result.coords;
        transient = result.transient;
        if (result.hit429) {
          rateLimitHit = true;
        }
        transientStreak = transient ? transientStreak + 1 : 0;
        if (transientStreak >= MAX_TRANSIENT_STREAK) {
          networkPaused = true;
          log.warn(
            `Nominatim unavailable for ${MAX_TRANSIENT_STREAK.toString()} places in a row — ` +
              'skipping lookups for the rest of this upload',
          );
        }
      }

      if (coords !== null) {
        this._applyCoords(group, coords);
        this._flagUnverified(normalized, group, opts.onUnverified);
        resolved++;
      } else if (transient) {
        // Left geocoded: false — not a failure; the next upload tries again.
        deferred++;
      } else {
        for (const loc of group) {
          loc.geocodeFailed = true;
          loc.geocoded = true;
        }
        failed++;
        opts.onFailure?.(group[0]?.raw ?? normalized);
      }

      report();
    }

    return locations;
  }

  /** Seed, then localStorage — ignoring any entry outside the region the key names. */
  private _cachedLookup(normalized: string): ResolvedCoords | null {
    const seedHit = this.seedCache.get(normalized);
    if (seedHit !== undefined) {
      if (withinRegion(normalized, seedHit.lat, seedHit.lng) !== false) {
        return seedHit;
      }
      log.warn(`Seed entry outside its named region ignored: "${normalized}"`);
    }

    const entry = this.geoCache.get(normalized);
    if (entry !== null) {
      if (withinRegion(normalized, entry.lat, entry.lng) !== false) {
        return entry.approximate === true
          ? { lat: entry.lat, lng: entry.lng, approximate: true }
          : { lat: entry.lat, lng: entry.lng };
      }
      log.warn(`Cached entry outside its named region ignored: "${normalized}"`);
    }

    return null;
  }

  private _applyCoords(group: Location[], coords: ResolvedCoords): void {
    for (const loc of group) {
      loc.lat = coords.lat;
      loc.lng = coords.lng;
      loc.geocoded = true;
      if (coords.approximate === true) {
        loc.approximate = true;
      }
    }
  }

  /** A place naming no region could not be region-checked — flag it for the user to check. */
  private _flagUnverified(
    normalized: string,
    group: Location[],
    onUnverified?: (raw: string) => void,
  ): void {
    if (expectedRegion(normalized) !== null) {
      return;
    }
    for (const loc of group) {
      loc.unverified = true;
    }
    onUnverified?.(group[0]?.raw ?? normalized);
  }

  /** Try each query variant in order; accept the first acceptable result. */
  private async _resolve(normalized: string): Promise<ResolveResult> {
    let hit429 = false;

    for (const variant of buildQueries(normalized)) {
      const fetched = await this._fetchNominatim(variant.query);
      if (fetched.hit429) {
        hit429 = true;
      }
      if (fetched.results === null) {
        // Transient — stop, and do not record a failure that a later upload could fix.
        return { coords: null, hit429, transient: true };
      }

      const { coords } = selectAcceptable(normalized, fetched.results);
      if (coords !== null) {
        const resolvedAt = new Date().toISOString();
        this.geoCache.set(
          normalized,
          variant.approximate
            ? { ...coords, resolvedAt, approximate: true }
            : { ...coords, resolvedAt },
        );
        log.debug(
          `Nominatim resolved (${variant.stage}): "${normalized}" → ` +
            `${coords.lat.toString()}, ${coords.lng.toString()}`,
        );
        return {
          coords: variant.approximate ? { ...coords, approximate: true } : coords,
          hit429,
          transient: false,
        };
      }
      log.debug(`Nominatim no acceptable result (${variant.stage}) for "${normalized}"`);
    }

    log.warn(`Nominatim could not resolve "${normalized}" — recorded as failed`);
    this.geoCache.setFailure(normalized);
    return { coords: null, hit429, transient: false };
  }

  private async _fetchNominatim(query: string, isRetry = false): Promise<FetchResult> {
    await this.rateLimiter.acquire();

    const url = new URL(NOMINATIM_BASE);
    url.searchParams.set('q', query);
    url.searchParams.set('format', 'jsonv2');
    url.searchParams.set('limit', '5');

    // No custom headers: a plain GET needs no CORS preflight, so a 429 arrives as a
    // readable status. The page's Referer identifies the app (Nominatim usage policy).
    let res: Response;
    try {
      res = await fetch(url.toString());
    } catch (err) {
      log.warn(`Nominatim network error for "${query}"`, err);
      return { results: null, hit429: false };
    }

    if (res.status === 429) {
      log.warn(
        `Nominatim 429 for "${query}"` + (isRetry ? ' (retry — giving up)' : ' — retrying in 2 s'),
      );
      if (!isRetry) {
        await new Promise<void>((resolve) => setTimeout(resolve, RETRY_DELAY_MS));
        const retryResult = await this._fetchNominatim(query, true);
        return { ...retryResult, hit429: true };
      }
      return { results: null, hit429: true };
    }

    if (!res.ok) {
      log.warn(`Nominatim HTTP ${res.status.toString()} for "${query}"`);
      return { results: null, hit429: false };
    }

    try {
      const data = (await res.json()) as unknown;
      if (!Array.isArray(data)) {
        log.warn(`Nominatim malformed response for "${query}"`);
        return { results: null, hit429: false };
      }
      return { results: data as NominatimResult[], hit429: false };
    } catch {
      log.warn(`Nominatim malformed response for "${query}"`);
      return { results: null, hit429: false };
    }
  }
}
